import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Clock, FileText, Shield, Award, Briefcase, Tag, Star, Globe, Users } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const iconMap = {
  Shield: Shield,
  Award: Award,
  Tag: Tag,
  Briefcase: Briefcase,
  FileText: FileText,
  Star: Star,
  Globe: Globe,
  Users: Users
};

const colorMap = {
  blue: "bg-blue-100 text-blue-800",
  green: "bg-green-100 text-green-800",
  purple: "bg-purple-100 text-purple-800",
  orange: "bg-orange-100 text-orange-800",
  red: "bg-red-100 text-red-800",
  gray: "bg-gray-100 text-gray-800",
  yellow: "bg-yellow-100 text-yellow-800",
  pink: "bg-pink-100 text-pink-800"
};

const Services = () => {
  const [filter, setFilter] = useState("all");
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [currentLanguage]);

  useEffect(() => {
    loadServices();
  }, [filter, currentLanguage]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [catRes, servRes] = await Promise.all([
        axios.get(`${API_URL}/api/service-categories`),
        axios.get(`${API_URL}/api/services?language=${currentLanguage}`)
      ]);
      setCategories(catRes.data);
      setServices(servRes.data);
    } catch (err) {
      console.error("Failed to load data:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async () => {
    try {
      const category = filter !== "all" ? filter : null;
      const url = category 
        ? `${API_URL}/api/services?language=${currentLanguage}&category=${category}`
        : `${API_URL}/api/services?language=${currentLanguage}`;
      const response = await axios.get(url);
      setServices(response.data);
    } catch (err) {
      console.error("Failed to load services:", err);
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || FileText;
    return <IconComponent className="h-6 w-6" />;
  };

  const getCategoryColor = (categoryKey) => {
    const cat = categories.find(c => c.key === categoryKey);
    return colorMap[cat?.color] || colorMap.gray;
  };

  const getCategoryName = (categoryKey) => {
    const cat = categories.find(c => c.key === categoryKey);
    if (cat) {
      return currentLanguage === "RU" ? cat.name_ru : cat.name_en;
    }
    return categoryKey;
  };

  const getCategoryIcon = (categoryKey) => {
    const cat = categories.find(c => c.key === categoryKey);
    return getIcon(cat?.icon || 'FileText');
  };

  if (loading) {
    return (
      <section id="services" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/3 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2 mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            {t('ourServices')}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('servicesDescription')}
          </p>
        </div>

        {/* Filter buttons - loaded from API */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10">
          <Button
            variant={filter === "all" ? "default" : "outline"}
            onClick={() => setFilter("all")}
            className={`text-sm sm:text-base ${filter === "all" ? "bg-orange-500 hover:bg-orange-600" : ""}`}
          >
            {currentLanguage === "RU" ? "Все услуги" : "All Services"}
          </Button>
          {categories.map((cat) => (
            <Button
              key={cat.key}
              variant={filter === cat.key ? "default" : "outline"}
              onClick={() => setFilter(cat.key)}
              className={`text-sm sm:text-base ${filter === cat.key ? "bg-orange-500 hover:bg-orange-600" : ""}`}
            >
              {currentLanguage === "RU" ? cat.name_ru : cat.name_en}
            </Button>
          ))}
        </div>

        {/* Services count */}
        <div className="text-center mb-8">
          <p className="text-gray-500">
            {currentLanguage === "RU" 
              ? `Найдено услуг: ${services.length}`
              : `Services found: ${services.length}`}
          </p>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.slice(0, 9).map((service) => (
            <Card 
              key={service.id} 
              className="group hover:shadow-xl transition-all duration-300 border-0 shadow-md cursor-pointer overflow-hidden"
              onClick={() => navigate(`/services/${service.slug}`)}
            >
              {/* Preview Image */}
              
              
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-orange-100 rounded-lg text-orange-600 group-hover:bg-orange-500 group-hover:text-white transition-colors duration-300">
                    {getCategoryIcon(service.category)}
                  </div>
                  <Badge className={`text-xs font-medium ${getCategoryColor(service.category)}`}>
                    {getCategoryName(service.category)}
                  </Badge>
                </div>
                <CardTitle className="text-xl font-semibold text-gray-900 group-hover:text-orange-500 transition-colors duration-300">
                  {service.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-gray-600 mb-4 leading-relaxed line-clamp-2">
                  {service.description}
                </p>
                
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <Clock className="h-4 w-4" />
                    <span>{service.timeframe}</span>
                  </div>
                  {service.price && (
                    <span className="text-sm font-medium text-green-600">{service.price}</span>
                  )}
                </div>

                <Button 
                  className="w-full bg-gray-900 hover:bg-orange-500 transition-colors duration-300"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/services/${service.slug}`);
                  }}
                >
                  {t('learnMoreBtn')}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Show more button */}
        {services.length > 9 && (
          <div className="text-center mt-8">
            <Button 
              variant="outline"
              size="lg"
              onClick={() => navigate('/services')}
              className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white"
            >
              {currentLanguage === "RU" 
                ? `Смотреть все услуги (${services.length})`
                : `View all services (${services.length})`}
            </Button>
          </div>
        )}

        {/* Call to action */}
        <div className="text-center mt-16">
          <div className="bg-white rounded-2xl p-8 shadow-lg max-w-4xl mx-auto">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">
              {t('notFoundService')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t('notFoundDescription')}
            </p>
            <Button 
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 px-8"
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {t('getConsultation')}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
